import React, { useState, useEffect } from 'react';

import addonsIcon from '../../../assets/icons/invoice-confirmation/addons.svg';
import fileIcon from '../../../assets/icons/invoice-confirmation/file.svg';
import filecutIcon from '../../../assets/icons/invoice-confirmation/filecut.svg';
import passIcon from '../../../assets/icons/invoice-confirmation/pass.svg';
import checkmarkIcon from '../../../assets/icons/invoice-confirmation/tick.svg';
import facebookIcon from '../../../assets/icons/social/facebook.svg';
import linkedinIcon from '../../../assets/icons/social/linkedin.svg';
import twitterIcon from '../../../assets/icons/social/twitter.svg';
import DateRange from '../../../shared/components/DateRange';
import { ORDER_TEXTS } from '../constants/order.constants';
import type { ConfirmationProps } from '../types/order.types';

// Types are imported from ../types/order.types — DisplayProduct, NextStep, ConfirmationProps


/**
 * Confirmation - Displays order confirmation for both standard and invoice/pay-later orders.
 *
 * @param {Object} orderData - Order object with details
 * @param {Array} products - Array of display products
 * @param {String} customerEmail - Customer email address
 * @param {Function} onReturnHome - Callback when return button is clicked
 * @param {Boolean} loading - Loading state
 * @param {String} error - Error message
 * @param {ReactNode} sidebar - Sidebar element (used for Desktop layout)
 * @param {ReactNode} hotelsNearby - Hotels Nearby element (used for Mobile/Tablet layout)
 * @param {Boolean} isInvoice - When true, renders invoice-specific messaging
 * @param {String} paymentDueDuration - Duration string shown for invoice payment due
 * @param {Array} nextSteps - Array of next steps
 */
const Confirmation: React.FC<ConfirmationProps> = ({
    orderData,
    products = [],
    customerEmail,
    onReturnHome,
    loading = false,
    error = null,
    sidebar = null,
    hotelsNearby = null,
    isInvoice = false,
    paymentDueDuration = ORDER_TEXTS.DEFAULT_DUE_DURATION,
    nextSteps = []
}) => {
    const [email, setEmail] = useState<string | null | undefined>(customerEmail);

    // Sync email state when prop changes
    useEffect(() => {
        if (customerEmail) {
            setEmail(customerEmail);
        }
    }, [customerEmail]);

    // Helper to format price
    const formatPrice = (centAmount: number | null | undefined, currencyCode = 'USD') => {
        if (centAmount === null || centAmount === undefined) return '';
        return new Intl.NumberFormat('en', {
            style: 'currency',
            currency: currencyCode
        }).format(centAmount / 100);
    };

    // Products are already processed by parent to include bundles
    const displayProducts = products;

    // Loading state
    if (loading) {
        return (
            <div className={`min-h-screen bg-gray-50 flex items-center justify-center`}>
                <div className="text-center">
                    <div className={`text-[gray-600] text-lg`}>{ORDER_TEXTS.LOADING}</div>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !orderData) {
        return (
            <div className={`min-h-screen bg-gray-50 flex items-center justify-center`}>
                <div className="text-center">
                    <h2 className={`text-[red-600] text-2xl font-bold mb-5`}>{error || ORDER_TEXTS.NOT_FOUND}</h2>
                    <button
                        onClick={onReturnHome}
                        className={`bg-[gray-900] text-white px-6 py-3 rounded-md font-semibold hover:bg-black transition-colors min-w-[200px]`}
                    >
                        {ORDER_TEXTS.RETURN_HOME}
                    </button>
                </div>
            </div>
        );
    }

    const orderNumber = String((orderData as Record<string, unknown>).orderNumber || (orderData as Record<string, unknown>).id || '');
    const currencyCode = String(((orderData as Record<string, Record<string, string>>).totalPrice)?.currencyCode || 'USD');

    const iconMap: Record<string, string> = {
        'filecut': filecutIcon,
        'file': fileIcon,
        'pass': passIcon,
    };

    return (
        <div className={`bg-[#F9FAFB] py-12 h-full px-[20px] min-[576px]:px-[24px]`}>
            <div className="w-full max-w-[350px] min-[576px]:max-w-[1240px] mx-auto min-h-screen">
                <div className={`grid grid-cols-1 ${sidebar ? 'min-[1025px]:grid-cols-[1fr_400px]' : ''} gap-[24px] h-full`}>

                    {/* Main Content Column — border-r sits at the column edge so
                         the 24 px grid gap gives exactly 24 px to the sidebar card. */}
                    <div className={`flex flex-col ${sidebar ? 'min-[1025px]:border-r min-[1025px]:border-gray-200' : ''}`}>
                        {/* Top Content */}
                        <div className={`${sidebar ? 'min-[1025px]:pr-8' : ''} w-full max-w-[720px]`}>
                            {/* Status Icon */}
                            <div className={`mb-8 p-4 bg-[#F2F4F7] rounded-full w-fit`}>
                                <img src={checkmarkIcon} alt={ORDER_TEXTS.SOCIAL_ALTS.SUCCESS} className="w-6 h-6 object-contain" />
                            </div>

                            {/* Title */}
                            <h1 className={`text-3xl md:text-4xl font-semibold text-[#101828] mb-2 leading-tight tracking-normal`}>
                                {ORDER_TEXTS.THANK_YOU_TITLE}
                            </h1>
                            <p className={`text-[#667085] text-base font-normal leading-relaxed mb-8 tracking-[0px]`}>
                                {ORDER_TEXTS.ORDER_NUMBER_PREFIX} {orderNumber}
                            </p>

                            {/* Description — differs between invoice and standard */}
                            <div className={`text-[#667085] text-base font-normal leading-relaxed mb-10 max-w-2xl space-y-4 tracking-[0px]`}>
                                {isInvoice ? (
                                    <>
                                        <p className={`text-base font-semibold text-[#182230] leading-relaxed tracking-[0px] mb-2`}>{ORDER_TEXTS.INVOICE_PENDING_TITLE}</p>
                                        <p>{ORDER_TEXTS.INVOICE_PENDING_DESC}</p>
                                        <p>{ORDER_TEXTS.INVOICE_DUE_PREFIX} {paymentDueDuration} {ORDER_TEXTS.INVOICE_DUE_SUFFIX}</p>
                                    </>
                                ) : (
                                    <p>{ORDER_TEXTS.STANDARD_PENDING_DESC}</p>
                                )}
                            </div>

                            {/* Next Steps */}
                            <h3 className={`text-base font-semibold text-[#182230] mb-6 leading-relaxed tracking-[0px]`}>{ORDER_TEXTS.NEXT_STEPS_TITLE}</h3>

                            <div className="border-gray-200">
                                {nextSteps && nextSteps.length > 0 && (
                                    nextSteps.map((step, index) => {
                                        const IconSrc = iconMap[step.icon] || fileIcon;

                                        return (
                                            <div key={index} className={`flex gap-4 py-6 ${index !== nextSteps.length - 1 ? 'border-b border-gray-200' : ''}`}>
                                                <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-900">
                                                    <img src={IconSrc} alt={step.title} className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className={`text-xs font-medium text-[#000F1A] leading-[1.5] tracking-[0px] mb-1`}>{step.title}</div>
                                                    <div className={`text-sm font-normal text-[#667085] leading-[1.5] tracking-[0px]`}>
                                                        {step.description}
                                                        {((step.title?.toLowerCase().includes('verification')) || (step.description?.toLowerCase().includes('sent to'))) && (
                                                            <> <span className={`text-[gray-900]`}>{email || ORDER_TEXTS.VERIFICATION_FALLBACK}</span></>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Bottom Content (No Border) */}
                        <div className="w-full max-w-[720px] min-[1025px]:pr-8">
                            {/* Booking Summary Card */}
                            <h3 className={`text-base font-semibold text-[#182230] mt-10 mb-4 leading-relaxed tracking-[0px]`}>{ORDER_TEXTS.YOUR_BOOKING_TITLE}</h3>

                            {displayProducts.length > 0 ? (
                                <div className="space-y-6">
                                    {displayProducts.map((product, index) => (
                                        <div key={product.id || index} className={`bg-white border border-[#D0D5DD] rounded-lg p-6 shadow-sm w-full max-w-[688px]`}>
                                            {(product.startDate || product.date || product.location) && (
                                                <div className={`text-xs font-normal text-[#667085] leading-[1.5] tracking-[0px] mb-1`}>
                                                    {/* Mobile View */}
                                                    <span className="block md:hidden">
                                                        {(product.startDate || product.date) && (
                                                            <div>
                                                                {product.startDate
                                                                    ? <DateRange startDate={product.startDate} endDate={product.endDate} />
                                                                    : product.date
                                                                }
                                                            </div>
                                                        )}
                                                        {product.location && <span className="block">{product.location}</span>}
                                                    </span>
                                                    {/* Desktop View */}
                                                    <span className="hidden md:inline">
                                                        <span>
                                                            {product.startDate
                                                                ? <DateRange startDate={product.startDate} endDate={product.endDate} />
                                                                : product.date
                                                            }
                                                        </span>
                                                        {(product.startDate || product.date) && product.location && ' • '}
                                                        {product.location}
                                                    </span>
                                                </div>
                                            )}

                                            {product.name && (
                                                <div className="mb-3">
                                                    <h4 className={`text-xl font-semibold text-[#030407] leading-tight tracking-[0px]`}>
                                                        {product.name}
                                                    </h4>
                                                </div>
                                            )}

                                            <div className="flex flex-row items-end justify-between gap-2 md:gap-4">
                                                <div className="flex items-center gap-2 md:gap-6">
                                                    <div className="flex items-center gap-1">
                                                        <img src={passIcon} alt="" className="w-5 h-5 flex-shrink-0" />
                                                        <span className={`text-lg font-semibold text-[#101828] leading-tight tracking-[0px] text-center whitespace-nowrap`}>
                                                            {product.quantity || 1} {((product.quantity || 1) > 1) ? ORDER_TEXTS.PASSES_LABEL : ORDER_TEXTS.PASS_LABEL}
                                                        </span>
                                                    </div>
                                                    {((product.addOnsCount || 0) > 0) && (
                                                        <div className="flex items-center gap-1">
                                                            <img src={addonsIcon} alt="" className="w-5 h-5 flex-shrink-0" />
                                                            <span className={`text-lg font-semibold text-[#101828] leading-tight tracking-[0px] text-center whitespace-nowrap`}>{product.addOnsCount} {ORDER_TEXTS.ADDONS_LABEL}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end flex-shrink-0">
                                                    {product.originalPrice != null && product.price != null && product.originalPrice > product.price && (
                                                        <span className={`text-xs font-normal text-[#667085] line-through leading-[1.5] tracking-[0px] text-right mb-1`}>
                                                            {formatPrice(product.originalPrice, currencyCode)}
                                                        </span>
                                                    )}
                                                    <div className={`text-lg font-semibold text-[#030407] leading-tight tracking-[0px] text-right`}>
                                                        {formatPrice(product.price, currencyCode)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-white border border-gray-300 rounded-lg p-6 text-gray-500 text-center">{ORDER_TEXTS.NO_BOOKING_DETAILS}</div>
                            )}

                            {/* Social Share — only for standard (non-invoice) orders */}
                            {!isInvoice && (
                                <div className="mt-8 flex w-full justify-start md:justify-end items-center gap-4">
                                    <span className="text-gray-500 text-sm">{ORDER_TEXTS.SHARE_TITLE}</span>
                                    <div className="flex gap-3">
                                        <a href="#" className="hover:opacity-80 transition-opacity">
                                            <img src={twitterIcon} alt={ORDER_TEXTS.SOCIAL_ALTS.TWITTER} className="w-5 h-5" />
                                        </a>
                                        <a href="#" className="hover:opacity-80 transition-opacity">
                                            <img src={linkedinIcon} alt={ORDER_TEXTS.SOCIAL_ALTS.LINKEDIN} className="w-5 h-5" />
                                        </a>
                                        <a href="#" className="hover:opacity-80 transition-opacity">
                                            <img src={facebookIcon} alt={ORDER_TEXTS.SOCIAL_ALTS.FACEBOOK} className="w-5 h-5" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Left-aligned on tablet, centred on mobile. */}
                            {/* Tablet / mobile divider (visible < 1025px) */}
                            <div className="mt-[24px] border-t border-gray-200 pt-[24px] min-[1025px]:hidden">
                                {hotelsNearby}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Column */}
                    {sidebar && (
                        <div className="h-full">
                            {sidebar}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Confirmation;
